import clsx from 'clsx'
import { useCallback, useMemo, useState } from 'react'

import { Payment } from '../../models/Payment'
import { User } from '../../models/User'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import Card from '../app/design/Card'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { Checkbox } from '../common/fields/Checkbox'
import { CurrencyField } from '../common/fields/CurrencyField'
import { NewChargeReceiptView } from './NewChargeReceiptView'

type Props = {
    payment: Payment
    onUpdatePayment: (payment: Payment) => void
}

export function NewChargeGratuityPage({ payment, onUpdatePayment }: Props) {
    const gratuityOptions: {
        value: string
        amount?: number
        label: string
        type: 'rate' | 'percent'
    }[] = useMemo(() => {
        const amount = payment.amount ?? 0

        const tenPercent = amount * 0.1
        const fifteenPercent = amount * 0.15
        const twentyPercent = amount * 0.2

        return [
            { value: '0', amount: 0, label: 'No tip', type: 'rate' },
            {
                value: '10',
                amount: 10,
                type: 'percent',
                label: `10% (${currencyFormat(tenPercent, true)})`,
            },
            {
                value: '15',
                amount: 15,
                type: 'percent',
                label: `15% (${currencyFormat(fifteenPercent, true)})`,
            },
            {
                value: '20',
                amount: 20,
                type: 'percent',
                label: `20% (${currencyFormat(twentyPercent, true)})`,
            },
            {
                value: 'custom',
                type: 'rate',
                label: 'Custom',
            },
        ]
    }, [payment])

    const defaultGratuity = useMemo(() => {
        if (!payment.gratuity) {
            return gratuityOptions.find((o) => o.value === '0')
        }

        if (payment.gratuityType !== 'percent') {
            return gratuityOptions.find((o) => o.value === 'custom')
        }

        return gratuityOptions.find((o) => o.amount === payment.gratuity)
    }, [payment, gratuityOptions])

    const [selectedGratuity, setSelectedGratuity] = useState<
        (typeof gratuityOptions)[number] | undefined
    >(defaultGratuity)

    const handleClick = useCallback(
        (o: (typeof gratuityOptions)[number]) => {
            setSelectedGratuity(o)
            if (o.value !== 'custom') {
                onUpdatePayment(payment.clone({ gratuity: o.amount, gratuity_type: o.type }))
            }
            else {
                onUpdatePayment(payment.clone({ gratuity: 0, gratuity_type: o.type }))
            }
        },
        [payment, selectedGratuity]
    )

    return (
        <div className="flex items-start justify-center gap-6">
            <Card size="lg" className="flex w-[744px] max-w-[744px] gap-4 rounded-lg bg-white p-8">
                <Icon icon="money.large" background="round" className="shrink-0" />
                <div className="flex w-full flex-col gap-3">
                    <div className="mb-[2px]">
                        <Label size="md" className="mb-1">
                            Gratuity
                        </Label>
                        <Paragraph size="sm" className="mt-[2px] text-gray-600">
                            Select
                        </Paragraph>
                    </div>

                    {gratuityOptions.map((o) => (
                        <div
                            key={o.label}
                            className={clsx(
                                'flex w-full cursor-pointer items-center justify-between rounded-lg px-4 outline outline-gray-300 hover:bg-black/4 active:bg-black/8',
                                {
                                    'outline-secondary-400 outline-2':
                                        selectedGratuity?.value === o.value,
                                },
                                {
                                    'py-4':
                                        o.value !== 'custom' ||
                                        selectedGratuity?.value !== 'custom',
                                },
                                {
                                    'py-[9px]':
                                        o.value === 'custom' &&
                                        selectedGratuity?.value === 'custom',
                                }
                            )}
                            onClick={() => handleClick(o)}
                        >
                            <Paragraph size="md" className="text-gray-700">
                                {o.label}
                            </Paragraph>

                            <div className="flex items-center gap-4">
                                {selectedGratuity?.value === o.value && o.value === 'custom' && (
                                    <CurrencyField
                                        key={payment.gratuity}
                                        size="small"
                                        value={payment.gratuity}
                                        className={clsx('text-right')}
                                        onFocus={(e) => {
                                            e.target.select()
                                        }}
                                        placeholder="0.00"
                                        onChange={(e) => {
                                            if (Number.isNaN(e)) {
                                                onUpdatePayment(
                                                    payment.clone({
                                                        gratuity: undefined,
                                                        gratuity_type: undefined,
                                                    })
                                                )
                                            }
                                            else {
                                                onUpdatePayment(
                                                    payment.clone({
                                                        gratuity: e,
                                                        gratuity_type: 'rate',
                                                    })
                                                )
                                            }
                                        }}
                                    />
                                )}
                                <Checkbox
                                    style="radio"
                                    isChecked={selectedGratuity?.value === o.value}
                                    onChange={() => {
                                        handleClick(o)
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
            <div className="flex w-[360px] flex-col gap-6">
                {payment.client !== undefined && (
                    <Card size="sm">
                        <Heading size="xs" className="mb-6">
                            Client
                        </Heading>
                        <ContactAvatar
                            user={payment.client ?? new User()}
                            subtitle={payment.subaccount?.name}
                            showName={true}
                            truncateName={true}
                        />
                    </Card>
                )}

                <NewChargeReceiptView payment={payment} />
            </div>
        </div>
    )
}
